import { useEffect, useState } from "react";
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import { Search, FileText, Hash, Link2, TrendingUp, TrendingDown, Minus, ArrowRight, Activity, Wrench, Rocket, Bot, Sparkles, Zap } from "lucide-react";
import { Link } from "react-router-dom";
import DashboardLayout from "@/components/DashboardLayout";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import { toast } from "sonner";

function StatCard({ icon: Icon, label, value, hint, testid }) {
  return (
    <div className="bg-zinc-950 border border-zinc-800 rounded-sm p-5 hover:border-zinc-700 transition-colors" data-testid={testid}>
      <div className="flex items-start justify-between mb-3">
        <span className="label-eyebrow">{label}</span>
        <Icon className="w-4 h-4 text-zinc-500" />
      </div>
      <div className="font-display text-3xl font-semibold tracking-tighter">{value}</div>
      {hint && <div className="text-xs text-zinc-500 mt-1">{hint}</div>}
    </div>
  );
}

function PipelineStep({ label }) {
  return (
    <div className="flex items-center gap-2 px-3 py-1.5 rounded-sm bg-zinc-950/80 border border-zinc-800 text-xs">
      <Zap className="w-3 h-3 text-[#3377FF]" />
      <span className="text-zinc-300">{label}</span>
    </div>
  );
}

export default function Dashboard() {
  const [stats, setStats] = useState(null);

  useEffect(() => {
    api.get("/dashboard/stats").then(({ data }) => setStats(data)).catch(() => {});
    // Toast on ?email_verified=1 (success) or =already (already verified)
    try {
      const params = new URLSearchParams(window.location.search);
      const v = params.get("email_verified");
      if (v === "1") {
        toast.success("Your email is now verified.", { duration: 5000 });
      } else if (v === "already") {
        toast.info("Your email was already verified.", { duration: 4000 });
      }
      if (v) {
        params.delete("email_verified");
        const q = params.toString();
        window.history.replaceState(null, "", window.location.pathname + (q ? `?${q}` : ""));
      }
    } catch { /* ignore */ }
  }, []);

  const score = stats?.avg_seo_score ?? 0;

  return (
    <DashboardLayout
      eyebrow="Mission Control"
      title="Welcome to RankPilot."
      subtitle="Your unified view of audits, content, keywords and backlinks."
      action={
        <Button asChild className="bg-[#0055FF] hover:bg-[#3377FF] rounded-sm h-10" data-testid="dashboard-cta-audit">
          <Link to="/audit">New SEO Audit <ArrowRight className="w-4 h-4 ml-1.5" /></Link>
        </Button>
      }
    >
      {/* Full Autopilot mega-CTA */}
      <section className="relative overflow-hidden border border-[#0055FF]/40 rounded-sm mb-6 group" data-testid="dashboard-autopilot-hero">
        <div className="absolute inset-0 bg-gradient-to-br from-[#0055FF]/20 via-[#0055FF]/5 to-transparent" />
        <div className="absolute inset-0 grain opacity-30" />
        <div className="relative p-6 sm:p-8 flex flex-col lg:flex-row lg:items-center gap-6">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-3">
              <span className="inline-flex items-center gap-1.5 text-[10px] tracking-[0.2em] uppercase text-[#3377FF] font-medium">
                <Sparkles className="w-3 h-3" /> ONE-CLICK PIPELINE
              </span>
            </div>
            <h2 className="font-display text-2xl sm:text-3xl font-semibold tracking-tight leading-tight">
              Run your entire SEO workflow on autopilot.
            </h2>
            <p className="text-sm text-zinc-400 mt-2 leading-relaxed max-w-xl">
              Business analysis → SEO audit → Auto-Fix Kit → content plan → 5 blogs generated → auto-publish → social broadcast. AI does everything, you close your laptop.
            </p>
            <div className="flex gap-3 flex-wrap mt-5">
              <Button asChild size="lg" className="bg-[#0055FF] hover:bg-[#3377FF] rounded-sm h-12 px-6 shadow-lg shadow-[#0055FF]/20" data-testid="dashboard-autopilot-cta">
                <Link to="/autopilot"><Rocket className="w-4 h-4 mr-2" /> Run Full Autopilot</Link>
              </Button>
              <Button asChild size="lg" variant="outline" className="bg-zinc-950/60 hover:bg-zinc-900 border-zinc-700 rounded-sm h-12 px-5" data-testid="dashboard-otto-cta">
                <Link to="/otto"><Bot className="w-4 h-4 mr-2" /> Install OTTO Live SEO</Link>
              </Button>
            </div>
          </div>
          <div className="hidden lg:flex items-center gap-2 flex-shrink-0">
            <div className="flex flex-col gap-2 text-right">
              <PipelineStep label="Analyze" />
              <PipelineStep label="Audit" />
              <PipelineStep label="Fix" />
            </div>
            <div className="flex flex-col gap-2">
              <PipelineStep label="Plan" />
              <PipelineStep label="Write" />
              <PipelineStep label="Publish" />
            </div>
          </div>
        </div>
      </section>

      {/* Stats grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 mb-6" data-testid="stats-grid">
        <StatCard icon={Search} label="Audits" value={stats?.counts?.audits ?? 0} testid="stat-audits" />
        <StatCard icon={FileText} label="Blog Posts" value={stats?.counts?.blogs ?? 0} testid="stat-blogs" />
        <StatCard icon={Hash} label="Keyword Searches" value={stats?.counts?.keyword_searches ?? 0} testid="stat-keywords" />
        <StatCard icon={Link2} label="Backlinks" value={stats?.counts?.backlinks ?? 0} testid="stat-backlinks" />
        <StatCard icon={Wrench} label="Free Tools" value={stats?.counts?.tools ?? 0} testid="stat-tools" />
      </div>

      {/* Health + traffic */}
      <div className="grid lg:grid-cols-3 gap-4 mb-6">
        {/* Health score */}
        <div className="bg-zinc-950 border border-zinc-800 rounded-sm p-6" data-testid="health-card">
          <div className="flex items-center justify-between mb-6">
            <span className="label-eyebrow">SEO Health</span>
            <Activity className="w-4 h-4 text-[#0055FF]" />
          </div>
          <div className="flex items-baseline gap-2">
            <span className="font-display text-7xl font-semibold tracking-tighter" data-testid="health-score">{score}</span>
            <span className="text-zinc-500 text-sm">/100</span>
          </div>
          <div className="mt-6 h-2 bg-zinc-900 rounded-sm overflow-hidden">
            <div className="h-full bg-gradient-to-r from-[#EF4444] via-[#F59E0B] to-[#10B981] transition-all" style={{ width: `${score}%` }} />
          </div>
          <p className="text-sm text-zinc-400 mt-4 leading-relaxed">
            {score === 0
              ? "Run your first audit to see your health score."
              : score >= 80
                ? "Strong. Maintain content velocity & monitor."
                : score >= 60
                  ? "Decent base. Tackle critical issues to break into top tier."
                  : "Lots of upside. Address technical & on-page issues first."}
          </p>
        </div>

        {/* Traffic chart */}
        <div className="lg:col-span-2 bg-zinc-950 border border-zinc-800 rounded-sm p-6" data-testid="traffic-card">
          <div className="flex items-center justify-between mb-4">
            <div>
              <span className="label-eyebrow">Organic Traffic — last 14 days</span>
              <p className="text-xs text-zinc-500 mt-1">Synthetic preview · connect Google Search Console for live data</p>
            </div>
          </div>
          <div className="h-56 min-h-[224px]" data-testid="traffic-chart">
            <ResponsiveContainer width="100%" height="100%" minWidth={100} minHeight={200}>
              <AreaChart data={stats?.traffic_series ?? []}>
                <defs>
                  <linearGradient id="g1" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#0055FF" stopOpacity={0.5} />
                    <stop offset="100%" stopColor="#0055FF" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#27272A" />
                <XAxis dataKey="day" stroke="#71717A" fontSize={11} />
                <YAxis stroke="#71717A" fontSize={11} />
                <Tooltip contentStyle={{ background: "#09090B", border: "1px solid #27272A", borderRadius: 4 }} />
                <Area type="monotone" dataKey="visits" stroke="#0055FF" strokeWidth={2} fill="url(#g1)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Rankings table */}
      <div className="bg-zinc-950 border border-zinc-800 rounded-sm" data-testid="rankings-card">
        <div className="px-6 py-4 border-b border-zinc-800 flex items-center justify-between">
          <span className="label-eyebrow">Top Tracked Keywords</span>
          <Link to="/keywords" className="text-xs text-[#3377FF] hover:text-white">Run research →</Link>
        </div>
        <table className="w-full">
          <thead>
            <tr className="text-left text-xs text-zinc-500 border-b border-zinc-800">
              <th className="px-6 py-3 font-medium">Keyword</th>
              <th className="px-6 py-3 font-medium">Position</th>
              <th className="px-6 py-3 font-medium">Change</th>
            </tr>
          </thead>
          <tbody>
            {(stats?.top_rankings ?? []).map((r) => (
              <tr key={r.keyword} className="border-b border-zinc-900 last:border-0 hover:bg-zinc-900/50 transition-colors">
                <td className="px-6 py-4 text-zinc-200">{r.keyword}</td>
                <td className="px-6 py-4 font-mono text-zinc-300">#{r.position}</td>
                <td className="px-6 py-4">
                  <ChangePill v={r.change} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </DashboardLayout>
  );
}

function ChangePill({ v }) {
  if (v > 0) {
    return <span className="inline-flex items-center gap-1 text-xs text-[#10B981] bg-[#10B981]/10 border border-[#10B981]/30 px-2 py-1 rounded-sm"><TrendingUp className="w-3 h-3" /> +{v}</span>;
  }
  if (v < 0) {
    return <span className="inline-flex items-center gap-1 text-xs text-[#EF4444] bg-[#EF4444]/10 border border-[#EF4444]/30 px-2 py-1 rounded-sm"><TrendingDown className="w-3 h-3" /> {v}</span>;
  }
  return <span className="inline-flex items-center gap-1 text-xs text-zinc-500 bg-zinc-900 border border-zinc-800 px-2 py-1 rounded-sm"><Minus className="w-3 h-3" /> 0</span>;
}
